import React from "react";
import SeoPage from "../../components/SeoPage";
import RELATED from "../../data/related";
import { faqsByTopic } from "../../data/faqs";

export default function CsvVsExcel() {
    return (
        <SeoPage
            title="CSV vs Excel (XLSX) — Which Should You Use?"
            description="CSV is plain text, universal, and lightweight. Excel files are feature-rich and proprietary. Full breakdown of when to use each format."
            breadcrumbs={[{ label: "Home", href: "/" }, { label: "CSV vs Excel" }]}
            eyebrow="/ comparison"
            h1="CSV vs Excel (XLSX): the real differences"
            heroLead="Both store tabular data. They look similar in a spreadsheet app. But under the hood they're built for completely different jobs — and using the wrong one wastes hours."
            sections={[
                { heading: "Quick verdict",
                    body: "Use CSV for transferring data between systems. Use Excel (.xlsx) for analysing data with formulas. They're not competitors — they're tools for different stages of the same workflow.",
                },
                { heading: "Side-by-side comparison",
                    table: {
                        headers: ["Feature", "CSV", "Excel (.xlsx)"],
                        rows: [
                            ["File type", "Plain text", "Binary (zipped XML)"],
                            ["Formulas", "No", "Yes"],
                            ["Multiple sheets", "No", "Yes"],
                            ["Formatting (colours, fonts)", "No", "Yes"],
                            ["Charts and images", "No", "Yes"],
                            ["Macros / VBA", "No", "Yes"],
                            ["Universal compatibility", "Yes — every tool reads CSV", "Microsoft / Office ecosystem"],
                            ["File size (10k rows)", "~500 KB", "~3–5 MB"],
                            ["Speed to open", "Instant", "Seconds"],
                            ["Edit on a phone", "Easy", "Painful"],
                            ["Risk of data corruption", "Low", "Medium (auto-formatting)"],
                            ["Future-proof", "Yes (50+ year format)", "Mostly (Office can deprecate)"],
                        ],
                    },
                },
                { heading: "When to use CSV",
                    bullets: [
                        "Importing data into another tool (CRM, ecommerce, ad platform)",
                        "Exporting data from a database or web app",
                        "Backing up structured data",
                        "Sharing data with someone who doesn't have Office",
                        "Feeding a script, AI pipeline, or automation",
                        "Long-term archival",
                    ],
                },
                { heading: "When to use Excel",
                    bullets: [
                        "Doing calculations with formulas",
                        "Building pivot tables, charts, dashboards",
                        "Conditional formatting and visual analysis",
                        "Multiple related tables in one file",
                        "Reports that need branding / formatting",
                    ],
                },
                { heading: "The classic CSV gotcha", body: "When Excel opens a CSV, it tries to be helpful. It converts ZIP-code columns to numbers (and drops leading zeros). It reformats dates based on your regional settings. It turns long numbers like credit card or order IDs into scientific notation. If you save the file back as CSV, those changes are now permanent. A purpose-built CSV editor avoids all of that — your data stays exactly as it was." },
                { heading: "What about Google Sheets?", body: "Sheets is the cloud equivalent of Excel. Same feature set, same gotchas with CSV opening. It's a great spreadsheet app — but for raw CSV editing, a dedicated CSV editor is faster and safer." },
            ]}
            faqs={faqsByTopic("csv-vs-excel", "differences", "general")}
            related={[RELATED.viewer, RELATED.editor, RELATED.what, RELATED.use, RELATED.impexp, RELATED.faq]}
        />
    );
}
